import { useState, useCallback, useEffect } from 'react'
import type { Transfer, Transaction } from './types'
import { fetchTransactionByHash } from './api'
import {
  DataTable,
  Spinner,
  AmountDisplay,
  formatNumber,
  truncate,
  formatTime,
  renderJsonData,
  LinkToCopy,
  walletUrl,
  txUrl,
  blockUrl,
  txHashToDisplayHex,
} from './components'

interface TransferGroup {
  block_height: number
  created_at: string
  transfers: Transfer[]
  totalAmount: number
  counterparties: string[]
}

function groupTransfersByBlock(transfers: Transfer[]): TransferGroup[] {
  const map = new Map<number, TransferGroup>()
  for (const t of transfers) {
    let group = map.get(t.block_height)
    if (!group) {
      group = {
        block_height: t.block_height,
        created_at: t.created_at,
        transfers: [],
        totalAmount: 0,
        counterparties: [],
      }
      map.set(t.block_height, group)
    }
    group.transfers.push(t)
    group.totalAmount += parseFloat(t.amount) || 0
    if (!group.counterparties.includes(t.recipient)) {
      group.counterparties.push(t.recipient)
    }
  }
  return Array.from(map.values()).sort((a, b) => b.block_height - a.block_height)
}

/**
 * Expandable content for a group of transfers sharing the same block.
 *
 * Shows two sections:
 * 1. Per-transfer breakdown — each transfer's counterparty, amount, denom, tx hash
 * 2. Transaction metadata — fetched by tx_hash from the first transfer that has one:
 *    status, gas wanted/used, message types, sender, memo, raw JSON
 *
 * If no transfer has a tx_hash (block-level event), only section 1 shows.
 */
function GroupExpansion({
  group,
  direction,
}: {
  group: TransferGroup
  direction: 'sent' | 'received'
}) {
  const counterpartyField = direction === 'sent' ? 'recipient' : 'sender'
  const counterpartyLabel = direction === 'sent' ? 'Recipient' : 'Sender'

  // Find the first transfer with a non-empty tx_hash
  const txHash = group.transfers.find((t) => t.tx_hash && t.tx_hash.length > 0)?.tx_hash ?? null

  const [tx, setTx] = useState<Transaction | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  const fetchTx = useCallback(async () => {
    if (!txHash || fetched) return
    setLoading(true)
    try {
      const result = await fetchTransactionByHash(txHash)
      setTx(result)
    } catch {
      // silent — show partial details
    } finally {
      setFetched(true)
      setLoading(false)
    }
  }, [txHash, fetched])

  useEffect(() => {
    if (txHash && !fetched && !loading) fetchTx()
  }, [txHash, fetched, loading, fetchTx])

  return (
    <div className="space-y-4 py-2">
      {/* Section 1: Per-transfer breakdown */}
      <div>
        <div className="text-xs text-text-faint mb-2">
          {group.transfers.length} transfer{group.transfers.length > 1 ? 's' : ''} in block{' '}
          <LinkToCopy text={String(group.block_height)} href={blockUrl(group.block_height)} display={<span>{group.block_height}</span>} />
        </div>
        <div className="space-y-2">
          {group.transfers.map((t, i) => (
            <div
              key={i}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] items-center gap-2 rounded border border-border-faint bg-surface px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-text-faint text-xs whitespace-nowrap">{counterpartyLabel}:</span>
                <LinkToCopy
                  text={t[counterpartyField]}
                  href={walletUrl(t[counterpartyField])}
                  display={
                    <span className="text-sm font-mono text-accent-text truncate">
                      {truncate(t[counterpartyField])}
                    </span>
                  }
                  className="hover:text-accent-text"
                />
              </div>
              <span className="text-sm font-mono text-amber-text whitespace-nowrap">
                <AmountDisplay value={t.amount} />
              </span>
              <span className="text-xs font-mono text-text-muted">{t.denom}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2: Transaction metadata (fields NOT already shown above) */}
      {txHash && (
        <div className="border-t border-border-faint pt-3">
          {loading ? (
            <div className="flex items-center gap-2 py-2">
              <Spinner size="sm" />
              <span className="text-xs text-text-muted">Fetching transaction details…</span>
            </div>
          ) : tx ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                <DetailItem label="TX Hash" value={txHashToDisplayHex(tx.tx_hash)} mono href={txUrl(txHashToDisplayHex(tx.tx_hash))} />
                <DetailItem
                  label="Status"
                  value={tx.status_code === 0 ? '✓ Success' : `✗ Code ${tx.status_code}`}
                  valueClass={tx.status_code === 0 ? 'text-success-text' : 'text-danger-text'}
                />
                <DetailItem label="Block Index" value={String(tx.block_index)} mono />
                <DetailItem label="Gas Wanted" value={formatNumber(tx.gas_wanted)} mono />
                <DetailItem
                  label="Gas Used"
                  value={tx.gas_wanted > 0
                    ? `${formatNumber(tx.gas_used)} (${((tx.gas_used / tx.gas_wanted) * 100).toFixed(1)}%)`
                    : formatNumber(tx.gas_used)}
                  mono
                />
              </div>

              {/* Message types */}
              <div>
                <span className="text-xs uppercase tracking-wider text-text-faint mr-2">Messages:</span>
                <div className="inline-flex flex-wrap gap-1">
                  {tx.message_types.map((mt, i) => (
                    <span
                      key={i}
                      className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs font-mono text-accent-text"
                    >
                      {mt}
                    </span>
                  ))}
                </div>
              </div>

              {/* Memo */}
              {tx.memo && (
                <div className="text-xs">
                  <span className="text-text-faint mr-1">Memo:</span>
                  <span className="font-mono text-text-muted">{tx.memo}</span>
                </div>
              )}

              {/* Raw JSON (collapsible) */}
              <div className="text-xs">
                <details>
                  <summary className="cursor-pointer text-text-faint hover:text-text-muted">
                    Raw JSON Data
                  </summary>
                  <pre className="mt-1 overflow-x-auto rounded bg-code-bg p-2 text-xs font-mono text-text-muted max-h-48">
                    {renderJsonData(tx.json_data)}
                  </pre>
                </details>
              </div>
            </div>
          ) : (
            <div className="text-xs text-text-faint">
              Transaction details unavailable for hash:{' '}
              <LinkToCopy
                text={txHashToDisplayHex(txHash)}
                href={txUrl(txHashToDisplayHex(txHash))}
                display={<span className="font-mono text-text-muted">{truncate(txHashToDisplayHex(txHash), 16, 8)}</span>}
                className="hover:text-accent-text"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DetailItem({
  label,
  value,
  mono,
  valueClass,
  href,
}: {
  label: string
  value: string
  mono?: boolean
  valueClass?: string
  href?: string
}) {
  return (
    <div>
      <div className="text-text-faint uppercase tracking-wider">{label}</div>
      <div className={`mt-0.5 ${mono ? 'font-mono' : ''} ${valueClass ?? 'text-text-secondary'}`}>
        {href ? (
          <LinkToCopy
            text={value}
            href={href}
            display={<span className="break-all">{truncate(value, 16, 8)}</span>}
            className="text-text-secondary hover:text-accent-text"
          />
        ) : (
          value
        )}
      </div>
    </div>
  )
}

/**
 * Transfer table that groups transfers by block height.
 *
 * Cosmos transactions can contain multiple MsgSend messages in a single tx,
 * producing multiple transfer rows with the same block_height. Grouping them
 * shows one row per block with total amount and count, expandable to see each
 * individual transfer + the full transaction metadata (gas, status, messages).
 */
export function GroupedTransferTable({
  transfers,
  direction,
  emptyMessage,
}: {
  transfers: Transfer[]
  direction: 'sent' | 'received'
  emptyMessage: string
}) {
  const groups = groupTransfersByBlock(transfers)
  const counterpartyField = direction === 'sent' ? 'recipient' : 'sender'
  const counterpartyLabel = direction === 'sent' ? 'Recipient' : 'Sender'

  // Recompute counterparties based on actual direction
  for (const g of groups) {
    g.counterparties = [...new Set(g.transfers.map((t) => t[counterpartyField]))]
  }

  return (
    <DataTable
      rows={groups}
      emptyMessage={emptyMessage}
      expandableKey="block_height"
      renderExpansion={(g) => <GroupExpansion group={g} direction={direction} />}
      columns={[
        { key: 'block_height', label: 'Block', render: (g) => <LinkToCopy text={String(g.block_height)} href={blockUrl(g.block_height)} display={<span>{formatNumber(g.block_height)}</span>} /> },
        {
          key: 'counterparties',
          label: counterpartyLabel + 's',
          render: (g) => {
            if (g.counterparties.length === 1) {
              return (
                <LinkToCopy
                  text={g.counterparties[0]}
                  href={walletUrl(g.counterparties[0])}
                  display={
                    <span className="text-accent-text">
                      {truncate(g.counterparties[0])}
                    </span>
                  }
                />
              )
            }
            return (
              <span className="text-text-muted">
                {g.counterparties.length} {counterpartyLabel.toLowerCase()}s
              </span>
            )
          },
        },
        {
          key: 'totalAmount',
          label: 'Total (ALLO)',
          render: (g) => (
            <AmountDisplay value={g.totalAmount} className="text-amber-text" />
          ),
        },
        {
          key: 'transfers',
          label: '#',
          render: (g) => <span className="text-text-muted">{g.transfers.length}</span>,
        },
        {
          key: 'created_at',
          label: 'Time',
          render: (g) => formatTime(g.created_at),
        },
      ]}
    />
  )
}
